import { ArrowDown01 } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { HistoryButton } from '@/features/audit-log';
import { ApiError } from '@/shared/api/api-error';
import { ErrorState } from '@/shared/components/error-state';
import { StatusBadge } from '@/shared/components/status-badge';

import {
  useDenominationOrder,
  useSaveDenominationOrder,
  useSuggestDenominationOrderByValue,
} from '../api/brands.queries';
import type { DenominationOrderItem } from '../types/brand.types';
import { OrderReason } from '@/shared/components/order-reason';
import { SortableOrderList } from '@/shared/components/sortable-order-list';

/**
 * The storefront order of a brand's denominations in one region
 * (catalogue.manage, all-country scope). Every denomination of the region is
 * listed, active or not, exactly in the order the backend returns, whatever
 * filters the Denominations table has on.
 *
 * "Sort by value" asks the backend for a suggestion (lowest value first) and
 * puts it in the editor; nothing is saved until "Save order". Saving sends
 * every id in the new order; the backend validates, numbers them 1..n and
 * audits it. Mount only while open.
 */
export function DenominationOrderDialog({
  brandId,
  brandName,
  regionId,
  regionName,
  onClose,
}: {
  brandId: number;
  brandName: string;
  regionId: number;
  regionName: string;
  onClose: () => void;
}) {
  const current = useDenominationOrder(brandId, regionId);
  const save = useSaveDenominationOrder(brandId, regionId);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-3 sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-1">
            <DialogTitle className="text-base">
              Denomination order — {brandName} · {regionName}
            </DialogTitle>
            <HistoryButton
              entityType="DenominationOrder"
              entityId={`${brandId}:${regionId}`}
              name={`the ${brandName} ${regionName} denomination order`}
            />
          </div>
          <DialogDescription className="text-xs">
            The order customers see this region&apos;s denominations in, in every country.
          </DialogDescription>
        </DialogHeader>
        {current.isLoading ? (
          <Skeleton className="h-48" />
        ) : current.error || !current.data ? (
          <ErrorState error={current.error} onRetry={() => void current.refetch()} />
        ) : (
          <DenominationOrderEditor
            // A refetch (e.g. after a refused, stale save) starts the editor afresh.
            key={current.dataUpdatedAt}
            brandId={brandId}
            regionId={regionId}
            initial={current.data}
            save={save}
            onClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function DenominationOrderEditor({
  brandId,
  regionId,
  initial,
  save,
  onClose,
}: {
  brandId: number;
  regionId: number;
  initial: DenominationOrderItem[];
  save: ReturnType<typeof useSaveDenominationOrder>;
  onClose: () => void;
}) {
  const [order, setOrder] = useState(initial);
  const [reason, setReason] = useState('');
  const suggest = useSuggestDenominationOrderByValue(brandId, regionId);
  const originalIndex = new Map(initial.map((denomination, index) => [denomination.id, index]));

  const moved = order.filter((item, index) => originalIndex.get(item.id) !== index).length;
  const unchanged = moved === 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 text-xs">
      <div className="flex items-center justify-between gap-2">
        <p className="text-muted-foreground">
          Drag a row by its handle, or use its menu. New denominations go at the end.
        </p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={suggest.isPending || save.isPending}
          // The backend's suggested order replaces the unsaved one, as returned.
          onClick={() => suggest.mutate(undefined, { onSuccess: setOrder })}
        >
          <ArrowDown01 className="size-4" aria-hidden />
          Sort by value
        </Button>
      </div>

      <SortableOrderList
        className="min-h-0 flex-1"
        listId={`denomination-order-${regionId}`}
        label="Denomination order"
        items={order}
        onChange={setOrder}
        originalIndex={originalIndex}
        itemLabel={(denomination) => denomination.name}
        disabled={save.isPending}
        renderItem={(denomination) => (
          <>
            <span className="truncate font-medium">{denomination.name}</span>
            {denomination.currencyCode && (
              <span className="text-muted-foreground">{denomination.currencyCode}</span>
            )}
            {!denomination.isActive && <StatusBadge variant="neutral">Inactive</StatusBadge>}
          </>
        )}
      />

      <OrderReason id="denomination-order-reason" value={reason} onChange={setReason} />
      {save.error instanceof ApiError && (
        <p role="alert" className="text-destructive">
          {save.error.message}
        </p>
      )}
      <div className="flex items-center justify-end gap-2">
        <span className="text-muted-foreground me-auto" aria-live="polite">
          {unchanged ? 'No changes yet.' : `${moved} denomination${moved === 1 ? '' : 's'} moved.`}
        </span>
        <Button type="button" size="sm" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={unchanged || reason.trim().length < 3 || save.isPending}
          onClick={() =>
            save.mutate(
              { denominationIds: order.map((item) => item.id), reason: reason.trim() },
              { onSuccess: onClose },
            )
          }
        >
          Save order
        </Button>
      </div>
    </div>
  );
}
